import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ProgressDessert } from 'components/members/components/ProgressDessert/ProgressDessert'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { getGetEmployersTodosQueryKey, useGetEmployersTodos, usePatchEmployersTodosId } from 'utils/web_api'

import { TodoItem } from './TodoItem'

function TodoList () {
  const queryClient = useQueryClient()
  const { data, isLoading } = useGetEmployersTodos({ category: 'onboarding' })

  const markTodoCompleted = usePatchEmployersTodosId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEmployersTodosQueryKey({ category: 'onboarding' }),
        })
      },
    },
  })

  const todos = data?.todos || []
  const [showCompleted, setShowCompleted] = useState(false)

  const pendingTodos = useMemo(() => {
    return todos.filter(todo => todo.status !== 'completed')
  }, [todos])

  const completedTodos = useMemo(() => {
    return todos.filter(todo => todo.status === 'completed')
  }, [todos])

  const completedCount = completedTodos.length
  const totalCount = todos.length
  const allCompleted = totalCount > 0 && completedCount === totalCount

  if (isLoading) {
    return (
      <div className='p-6'>
        <SkeletonItem widthClass='w-full' />
      </div>
    )
  }

  if (todos.length === 0) {
    return (
      <div className='p-6'>
        <div className='mb-4 flex items-center justify-between'>
          <p className='body-bold'>
            You've done everything needed
          </p>
          <div className='flex items-center gap-x-4'>
            <p>All tasks complete</p>
            <ProgressDessert
              completed={1}
              total={1}
              size={32}
              strokeWidth={6}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='p-6'>
      <div className='mb-4 flex items-center justify-between'>
        <p className='body-bold'>
          {allCompleted ? "You've done everything needed" : "Here's what you need to do for your next bill"}
        </p>
        <div className='flex items-center gap-x-4'>
          <p>{allCompleted ? 'All tasks complete' : `${completedCount} of ${totalCount} tasks completed`}</p>
          <ProgressDessert
            completed={completedCount}
            total={totalCount}
            size={32}
            strokeWidth={6}
          />
        </div>
      </div>

      <div className='space-y-2'>
        {pendingTodos.map((todo) => (
          <TodoItem
            key={todo.id}
            todo={todo}
            onMarkComplete={(id) => markTodoCompleted.mutate({ id })}
          />
        ))}

        {completedCount > 0 && (
          <>
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className='w-full flex items-center justify-start gap-2 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors'
            >
              <span>{completedCount} completed task{completedCount !== 1 ? 's' : ''}</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${showCompleted ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>

            {showCompleted && (
              <div className='space-y-2'>
                {completedTodos.map((todo) => (
                  <TodoItem
                    key={todo.id}
                    todo={todo}
                    onMarkComplete={(id) => markTodoCompleted.mutate({ id })}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default TodoList
